
import React, { useState } from 'react';
import { SectionTitle, ScrollNavigation } from './Components';

interface TeamMember {
  name: string;
  role: string;
  qualifications: string;
  experience?: string;
  gender: 'male' | 'female';
  image?: string;
}

const TeamCard: React.FC<{ member: TeamMember }> = ({ member }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 group h-full flex flex-col items-center">
      <div className="relative mb-8 pt-4">
        <div className="absolute inset-0 bg-[#008080]/5 rounded-full scale-110 blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
        {member.image && !imgError ? (
            <div className="w-48 h-48 rounded-[3rem] shadow-xl overflow-hidden bg-slate-50 border-4 border-white relative z-10 transition-transform duration-500 group-hover:scale-105 mx-auto">
              <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={() => setImgError(true)}
              />
            </div>
        ) : (
            <div className={`w-48 h-48 rounded-[3rem] flex items-center justify-center text-6xl shadow-xl bg-indigo-50 text-indigo-500 border-4 border-white relative z-10 transition-transform duration-500 group-hover:scale-105 mx-auto`}>
               <i className={`fa-solid ${member.gender === 'male' ? 'fa-user-doctor' : 'fa-user-nurse'}`}></i>
            </div>
        )}
      </div>

      <div className="flex flex-col items-center text-center w-full">
        <div className="min-h-[3.5rem] flex flex-col justify-center mb-2">
            <h3 className="text-xl font-black text-slate-900 leading-tight group-hover:text-[#008080] transition-colors">{member.name}</h3>
        </div>
        
        <div className="min-h-[2.5rem] flex items-center mb-6">
            <p className="text-[#008080] text-[10px] font-black uppercase tracking-[0.2em] bg-[#008080]/5 px-4 py-1.5 rounded-full">{member.role}</p>
        </div>
        
        <div className="w-16 h-[3px] bg-slate-100 rounded-full mb-8 group-hover:w-24 group-hover:bg-[#008080]/20 transition-all duration-500"></div>
        
        <div className="w-full flex-grow flex flex-col">
            <h4 className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 text-center shrink-0">Expertise & Certifications</h4>
            <div className="flex flex-col space-y-2.5 max-h-[160px] overflow-y-auto pr-2 scrollbar-thin">
            {member.qualifications.split('\n').map((q, i) => (
                <div key={i} className="flex items-start gap-2.5 px-3 mb-1">
                <span className="text-[#008080] mt-1 shrink-0 text-[14px] leading-[1]">🩺</span>
                <span className="text-left leading-tight text-slate-600 text-[13px] font-bold">{q}</span>
                </div>
            ))}
            </div>
        </div>
        
        {member.experience && (
            <div className="mt-8 pt-6 border-t border-slate-50 w-full shrink-0">
                <p className="text-slate-400 text-[11px] font-bold uppercase tracking-wider leading-relaxed italic line-clamp-3 hover:line-clamp-none transition-all duration-300 cursor-help" title={member.experience}>{member.experience}</p>
            </div>
        )}

      </div>
    </div>
  );
};

export const LeadershipPage: React.FC = () => {
  const clinicians: TeamMember[] = [
    { 
      name: "Dr. Mohammad Yousuf Awan", 
      role: "Founder & CEO", 
      qualifications: "MBBS\nFCPS Pakistan\nCESR/CCST (UK)\nFACP (USA)\nMember Academy of Medical Educator (UK)", 
      experience: "Consultant General Internal Medicine/ Acute Medicine/ Geriatics (United Kingdom)",
      gender: "male",
      image: "/Dr.Yousuf.png"
    },
    { 
      name: "Dr. Rameesha Awan", 
      role: "CMO | Member Advisory Board", 
      qualifications: "MBBS (UK)\nMember Advisory Board", 
      gender: "female",
      image: "/Rameesha.jpeg"
    },
    { 
      name: "Dr. Ahmad Arslan", 
      role: "Member Advisory Board", 
      qualifications: "MBBS\nMD (USA)\nBoard Certified in Internal Medicine (USA)\nBoard Certified in Nephrology (USA)",
      experience: "Consultant, Nephrologist, Director Nephrology Baylor College of Medicine", 
      gender: "male",
      image: "/Dr. Arslan.jpeg"
    },
    { 
      name: "Dr. Ahmad Armoghan", 
      role: "Member Advisory Board", 
      qualifications: "MD (USA)\nBoard Certified in Internal Medicine\nBoard Certified in Interventional Cardiology (USA)\nBoard Certified in Echocardiography (USA)\nBoard Certified in Cardiovascular disease\nBoard Certified in Endovascular Medicine\nBoard Certified in Nuclear Cardiology\nRegistered Physician in Vascular Interpretation\nFACC\nFSCAI\nFSVM", 
      experience: "Interventional Cardiologist, Lake Charles Memorial Hospital",
      gender: "male",
      image: "/Dr. Armoghan.jpeg"
    }
  ];

  const operational: TeamMember[] = [
    {
      name: "Mahwish Yousuf Awan",
      role: "COO & Legal Advisor",
      qualifications: "MPH (USA)\nCPCJ (USA)\nLLB Pakistan\nBML",
      gender: "female",
      image: "/Mahwish.jpeg"
    }
  ];

  const technical: TeamMember[] = [
    {
      name: "Muhammad Hunzla Ayub Awan",
      role: "CTO",
      qualifications: "Oracle Certified Infrastructure Architect\nOracle Certified Multicloud Architect Professional\nMasters (IT)",
      gender: "male",
      image: "/M. Hunzla Awan.jpeg"
    },
    {
      name: "Dr. Arjmand Naveed",
      role: "CIO",
      qualifications: "PhD AI & ML\nMSC Advance Computer Sciences (UK)\nMCS (Pakistan)",
      experience: "Lecturer Arden University, Leeds, UK",
      gender: "female",
      image: "/Arjman.jpeg"
    }
  ];

  return (
    <div id="about-section" className="py-16 max-w-7xl mx-auto px-6 animate-in fade-in duration-700">
      <div className="mb-24">
        <div className="text-center mb-12">
           <SectionTitle centered title="About Us" subtitle="Bridging the gap between clinical excellence and digital innovation." />
        </div>
        <div className="max-w-4xl mx-auto relative bg-white/70 backdrop-blur-md p-10 md:p-14 rounded-3xl border border-slate-200 shadow-sm text-center mb-24">
            <p className="text-lg md:text-xl text-slate-700 font-medium leading-relaxed mb-6">
              Our aim is to make quality healthcare accessible, convenient, and reliable, no matter where you live. Through secure virtual consultations, our panel of internationally trained physicians offers professional evaluation, guidance, and support for a wide range of medical conditions.
            </p>
            <p className="text-lg md:text-xl text-slate-700 font-medium leading-relaxed">
              We are committed to honesty, confidentiality, and patient-centered care, helping you make informed decisions about your health with confidence.
            </p>
        </div>

        <div className="text-center mb-16">
           <SectionTitle centered title="Our Team" />
        </div>
      </div>

      {/* Clinicians */}
      <div id="board-section" className="mb-24">
        <div className="flex items-center gap-6 mb-12">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white text-3xl shadow-lg shadow-indigo-500/30 rotate-3 transform hover:rotate-6 transition-transform">
             <i className="fa-solid fa-stethoscope"></i>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-slate-900 tracking-tight leading-none mb-2">Clinical Board</h3>
            <p className="text-[#008080] font-semibold tracking-wider uppercase text-xs mt-2">World-Class Medical Experts</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {clinicians.map((m, i) => <TeamCard key={i} member={m} />)}
        </div>
        <ScrollNavigation nextId="operations-section" prevId="about-section" />
      </div>

      {/* Operations */}
      <div id="operations-section" className="mb-24">
        <div className="flex items-center gap-6 mb-12">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center text-white text-3xl shadow-lg shadow-slate-900/30 -rotate-3 transform hover:-rotate-6 transition-transform">
             <i className="fa-solid fa-building-user"></i>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-slate-900 tracking-tight leading-none mb-2">Operational Leadership</h3>
            <p className="text-slate-500 font-semibold tracking-wider uppercase text-xs mt-2">Strategic Direction & Compliance</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {operational.map((m, i) => <TeamCard key={i} member={m} />)}
        </div>
        <ScrollNavigation nextId="technical-section" prevId="board-section" />
      </div>

      {/* Technical */}
      <div id="technical-section" className="mb-24">
        <div className="flex items-center gap-6 mb-12">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-3xl shadow-lg shadow-emerald-500/30 rotate-3 transform hover:rotate-6 transition-transform">
             <i className="fa-solid fa-microchip"></i>
          </div>
          <div>
            <h3 className="text-3xl font-bold text-slate-900 tracking-tight leading-none mb-2">Technical Engineering</h3>
            <p className="text-emerald-600 font-semibold tracking-wider uppercase text-xs mt-2">Platform Innovation & Security</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {technical.map((m, i) => <TeamCard key={i} member={m} />)}
        </div>
        <ScrollNavigation nextId="footer" prevId="operations-section" isLast />
      </div>
    </div>
  );
};
